import { XIcon } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect } from "react";

type Props = {
  visible: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  showClose?: boolean;
  backdropClose?: boolean;
  noPadding?: boolean;
};

export const Modal = ({
  visible = false,
  onClose,
  children,
  showClose = true,
  backdropClose = true,
  noPadding = false,
}: Props) => {
  const onEscapeHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();

        onClose?.();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onEscapeHandler);
      return () => {
        document.body.style.overflow = "unset";
        document.removeEventListener("keydown", onEscapeHandler);
      };
    }
  }, [visible, onEscapeHandler]);

  const onBackdropClose = (e: any) => {
    if (backdropClose) {
      e.stopPropagation();

      onClose?.();
    }
  };

  return (
    <div
      className={`fixed top-0 right-0 left-0 z-50 w-full h-full md:inset-0 justify-center items-center backdrop-blur ${visible ? "flex" : "hidden"
        }`}
      onClick={onBackdropClose}
      style={{
        background: "rgba(0,0,0,0.1)",
      }}
      aria-modal="true"
      role="dialog"
    >
      <div className={`relative max-w-[95vw] max-h-[95vh] overflow-auto scroll-container no-scrollbar bg-white rounded-lg ${noPadding ? "p-0" : "p-14"}`}>
        {showClose && (
          <button
            type="button"
            className="group absolute right-4 top-4"
            onClick={onClose}
          >
            <XIcon className="w-6 h-6 stroke-gray-500 group-hover:stroke-black transition-colors" />
            <span className="sr-only">Close modal</span>
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

Modal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  showClose: PropTypes.bool,
  backdropClose: PropTypes.bool,
  children: PropTypes.node.isRequired,
};
